import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import App from './App'
import './app/globals.css'
import './styles/design-system.css'

// ─────────────────────────────────────────────────────────────────────────────
// Root Error Boundary
// Without this, React's default behaviour on an uncaught render error is to
// unmount the entire tree silently → blank page. This makes every future
// failure self-reporting: the user sees a visible message + Reload button,
// and the stack is logged to console for debugging.
// ─────────────────────────────────────────────────────────────────────────────
class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; message: string }
> {
  state = { hasError: false, message: '' }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error?.message ?? 'Unknown error' }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ROOT_BOUNDARY] Uncaught render error:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          background: '#141822ff',
          color: '#ffffff',
          fontFamily: 'system-ui, sans-serif',
          padding: 24,
        }}>
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🌿</div>
            <p style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
              Something went wrong loading the page.
            </p>
            <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
              {this.state.message}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 28px',
                borderRadius: 12,
                border: 'none',
                background: '#4a7c5e',
                color: '#fff',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mount
// hydrateRoot removed — this is a Vite SPA, #root is always empty in served
// HTML. hydrateRoot against non-React DOM (bfcache restore, CDN error pages)
// causes a hydration-mismatch fight that can blank the tree silently.
//
// RootErrorBoundary is outermost so it catches errors from all providers,
// including I18nProvider, AuthProvider, and the router itself.
// ─────────────────────────────────────────────────────────────────────────────
ReactDOM.createRoot(document.getElementById('root')!).render(
  <RootErrorBoundary>
    <React.StrictMode>
      <HelmetProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </HelmetProvider>
    </React.StrictMode>
  </RootErrorBoundary>
)

// ─────────────────────────────────────────────────────────────────────────────
// Service Worker registration
// Single owner: this file. The registration in index.html (inline script) has
// been removed so there is exactly one registration call and one place to
// reason about SW lifecycle.
//
// The controllerchange listener and SKIP_WAITING flow live in PWAUpdateHelper,
// which is the sole owner of the "new version available → reload" moment.
// Do NOT add a second controllerchange listener here — two owners = stampede.
// ─────────────────────────────────────────────────────────────────────────────
if ('serviceWorker' in navigator && (import.meta as any).env?.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[SW] Registration failed:', err)
    })
  })
}
